import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';

import type { ServerErrorBody } from '../../types/server-error-body';

/**
 * Maps a server-returned error envelope onto a React Hook Form instance.
 *
 * - If `body.fieldErrors` is present and non-empty, each entry is matched
 *   against the form's known field names (from `form.getValues()`). Matching
 *   entries are set on their field via `form.setError(field, ...)`. Entries
 *   whose key doesn't correspond to a known form field route to `'root'` so
 *   the banner surfaces them — otherwise they'd land silently in RHF's
 *   internal state with no display surface (e.g. server returns
 *   `fieldErrors.accountName` but the form's first/last-name UI doesn't
 *   register an `accountName` field).
 * - `options.unrendered` names registered fields the form is not showing right
 *   now (BL-2352: the Land section's pricing basis on a non-Land item). Their
 *   entries route to `'root'` like an unknown key, because `setError` on a
 *   hidden field has no display surface either. An entry matches a top-level
 *   field (every key under it) or one full key (BUG-2082: `customFields.3`, a
 *   custom-field slot the lot form is not showing).
 * - When `body.fieldErrors` is absent or empty, the full `body.error` string
 *   (which already embeds `requestId` for 5xx envelopes per `apiHandler` /
 *   `formatErrorMessage`) lands on `'root'`, which forms render as the
 *   top-of-form banner.
 *
 * @param form - The React Hook Form instance.
 * @param body - The server's error envelope.
 * @param options - `unrendered`: registered field names or full keys with no visible input.
 * @returns Nothing.
 * @sideEffect Calls `form.setError(...)`. Does not throw.
 */
export function applyServerErrors<
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  form: UseFormReturn<TFieldValues, TContext, TTransformedValues>,
  body: ServerErrorBody,
  options: { unrendered?: readonly string[] } = {},
): void {
  if (body.fieldErrors && Object.keys(body.fieldErrors).length > 0) {
    const unrendered = new Set(options.unrendered ?? []);
    const knownFields = new Set(Object.keys(form.getValues()).filter((name) => !unrendered.has(name)));
    const orphanMessages: string[] = [];
    for (const [field, message] of Object.entries(body.fieldErrors)) {
      // RHF accepts setError on any path; un-rendered ones land in state
      // with no display surface. Route them to root instead so the operator
      // sees the error somewhere.
      if (knownFields.has(field.split('.')[0]) && !unrendered.has(field)) {
        form.setError(field as Path<TFieldValues>, {
          type: 'server',
          message,
        });
      } else {
        orphanMessages.push(message);
      }
    }
    if (orphanMessages.length > 0) {
      form.setError('root', {
        type: 'server',
        message: orphanMessages.join(' '),
      });
    }
    return;
  }
  form.setError('root', { type: 'server', message: body.error });
}
